import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ArrowLeft, LogOut, Plus, Trash2, Pencil, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full rounded-lg border border-[#EBDDC6] bg-[#FDF6EC] px-3 py-2.5 text-sm text-[#3B2A1E] placeholder-[#A08B74] outline-none focus:border-[#E07A2B]";

const STATUS_LABELS = { en_attente: "En attente", confirmee: "Confirmée", annulee: "Annulée" };
const ORDER_STATUS_LABELS = { en_attente: "En attente", confirmee: "Confirmée", prete: "Prête", annulee: "Annulée" };
const CAT_LABELS = { entrees: "Entrées", plats: "Plats signatures", sides: "Accompagnements", drinks: "Desserts & Boissons" };
const EMPTY_DISH = { name_fr: "", name_nl: "", desc_fr: "", desc_nl: "", price: "", category: "plats", spicy: false, signature: false, image: "" };

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("rd_admin_token"));
  const [tab, setTab] = useState("reservations");
  const [reservations, setReservations] = useState([]);
  const [orders, setOrders] = useState([]);
  const [dishes, setDishes] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");

  const auth = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const load = useCallback(() => {
    if (!token) return;
    axios.get(`${API}/admin/reservations`, auth()).then((r) => setReservations(r.data)).catch(() => setToken(null));
    axios.get(`${API}/admin/orders`, auth()).then((r) => setOrders(r.data)).catch(() => {});
    axios.get(`${API}/dishes`).then((r) => setDishes(r.data)).catch(() => {});
  }, [token, auth]);

  useEffect(load, [load]);

  const login = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, loginForm);
      localStorage.setItem("rd_admin_token", data.token);
      setToken(data.token);
    } catch (err) {
      const d = err.response?.data?.detail;
      setLoginError(typeof d === "string" ? d : "Connexion impossible");
    }
  };

  const logout = () => {
    localStorage.removeItem("rd_admin_token");
    setToken(null);
  };

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/reservations/${id}`, { status }, auth());
      setReservations((rs) => rs.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success("Statut mis à jour");
    } catch {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const exportDishes = () => {
    const header = ["Nom (FR)", "Nom (NL)", "Description (FR)", "Description (NL)", "Prix (EUR)", "Prix carte sur place (EUR)", "Categorie", "Releve", "Signature", "Image"];
    const rows = dishes.map((d) => [
      d.name_fr, d.name_nl, d.desc_fr, d.desc_nl,
      (d.price * 1.25).toFixed(2), d.price.toFixed(2),
      CAT_LABELS[d.category] || d.category,
      d.spicy ? "Oui" : "Non", d.signature ? "Oui" : "Non",
      d.image ? `https://restaurant-lesretrouvaillesdeladouce.be${d.image}` : "",
    ]);
    const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const csv = "\uFEFF" + [header, ...rows].map((r) => r.map(esc).join(";")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "carte-les-retrouvailles-de-la-douce.csv";
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Carte exportee en CSV");
  };

  const setOrderStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/orders/${id}`, { status }, auth());
      setOrders((os) => os.map((o) => (o.id === id ? { ...o, status } : o)));
      toast.success("Statut mis à jour");
    } catch {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const removeOrder = async (id) => {
    try {
      await axios.delete(`${API}/admin/orders/${id}`, auth());
      setOrders((os) => os.filter((o) => o.id !== id));
      toast.success("Commande supprimée");
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  const removeReservation = async (id) => {
    try {
      await axios.delete(`${API}/admin/reservations/${id}`, auth());
      setReservations((rs) => rs.filter((r) => r.id !== id));
      toast.success("Réservation supprimée");
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  const saveDish = async (e) => {
    e.preventDefault();
    const payload = { ...editing, price: Number(editing.price), image: editing.image || null };
    try {
      if (editing.id) await axios.put(`${API}/admin/dishes/${editing.id}`, payload, auth());
      else await axios.post(`${API}/admin/dishes`, payload, auth());
      toast.success("Plat enregistré");
      setEditing(null);
      const { data } = await axios.get(`${API}/dishes`);
      setDishes(data);
    } catch {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const removeDish = async (id) => {
    try {
      await axios.delete(`${API}/admin/dishes/${id}`, auth());
      setDishes((ds) => ds.filter((d) => d.id !== id));
      toast.success("Plat supprimé");
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FDF6EC] px-4" data-testid="admin-login-page">
        <form onSubmit={login} className="w-full max-w-sm rounded-2xl border border-[#EBDDC6] bg-[#FFFBF3] p-8" data-testid="admin-login-form">
          <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-[#A9761F]">Les Retrouvailles de la Douce</p>
          <h1 className="font-display mt-2 text-2xl font-semibold text-[#3B2A1E]">Espace Admin</h1>
          <input type="email" required placeholder="Email" value={loginForm.email}
            onChange={(e) => setLoginForm((f) => ({ ...f, email: e.target.value }))}
            className={`${inputCls} mt-6`} data-testid="admin-login-email" />
          <input type="password" required placeholder="Mot de passe" value={loginForm.password}
            onChange={(e) => setLoginForm((f) => ({ ...f, password: e.target.value }))}
            className={`${inputCls} mt-3`} data-testid="admin-login-password" />
          {loginError && <p className="mt-3 text-sm text-red-600" data-testid="admin-login-error">{loginError}</p>}
          <button type="submit" className="mt-5 w-full rounded-full bg-[#E07A2B] py-3 text-sm font-semibold text-[#3B2A1E] transition-colors hover:bg-[#E89B4E]" data-testid="admin-login-submit">
            Se connecter
          </button>
          <Link to="/" className="mt-4 flex items-center justify-center gap-1.5 text-xs text-[#A08B74] hover:text-[#A9761F]" data-testid="admin-back-home">
            <ArrowLeft size={13} /> Retour au site
          </Link>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDF6EC] text-[#3B2A1E]" data-testid="admin-dashboard">
      <header className="border-b border-[#EBDDC6] bg-[#FFFBF3]">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-4">
          <h1 className="font-display text-xl font-semibold">Espace Admin</h1>
          <div className="flex items-center gap-2">
            {["reservations", "orders", "menu"].map((tb) => (
              <button key={tb} onClick={() => setTab(tb)} data-testid={`admin-tab-${tb}`}
                className={`rounded-full px-4 py-2 text-sm transition-colors ${tab === tb ? "bg-[#E07A2B] font-semibold text-[#3B2A1E]" : "text-[#7A6753] hover:text-[#3B2A1E]"}`}>
                {tb === "reservations" ? "Réservations" : tb === "orders" ? "Commandes" : "La Carte"}
              </button>
            ))}
            <Link to="/" className="rounded-full border border-[#EBDDC6] px-4 py-2 text-sm text-[#7A6753] hover:text-[#3B2A1E]" data-testid="admin-view-site">Voir le site</Link>
            <button onClick={logout} className="inline-flex items-center gap-1.5 rounded-full border border-[#EBDDC6] px-4 py-2 text-sm text-[#7A6753] hover:text-red-600" data-testid="admin-logout">
              <LogOut size={14} /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-8">
        {tab === "reservations" && (
          <div data-testid="admin-reservations-panel">
            {reservations.length === 0 ? (
              <p className="py-16 text-center text-[#A08B74]" data-testid="admin-reservations-empty">Aucune réservation pour le moment.</p>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-[#EBDDC6]">
                <table className="w-full min-w-[760px] text-left text-sm">
                  <thead className="bg-[#FFFBF3] text-xs uppercase tracking-wider text-[#A08B74]">
                    <tr>
                      <th className="px-4 py-3">Date & heure</th>
                      <th className="px-4 py-3">Nom</th>
                      <th className="px-4 py-3">Contact</th>
                      <th className="px-4 py-3">Convives</th>
                      <th className="px-4 py-3">Table</th>
                      <th className="px-4 py-3">Statut</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#EBDDC6]/60">
                    {reservations.map((r) => (
                      <tr key={r.id} className="bg-[#FDF6EC]" data-testid={`admin-reservation-row-${r.id}`}>
                        <td className="px-4 py-3 font-mono2 text-[#A9761F]">{r.date} · {r.time}</td>
                        <td className="px-4 py-3">
                          <p className="font-medium">{r.name}</p>
                          {r.notes && <p className="mt-0.5 max-w-[200px] truncate text-xs text-[#A08B74]" title={r.notes}>{r.notes}</p>}
                        </td>
                        <td className="px-4 py-3 text-[#7A6753]">
                          <p>{r.phone}</p>
                          {r.email && <p className="text-xs">{r.email}</p>}
                        </td>
                        <td className="px-4 py-3">{r.guests}</td>
                        <td className="px-4 py-3 text-[#7A6753]">{r.seating === "causerie" ? "Causerie" : "Intérieur"}</td>
                        <td className="px-4 py-3">
                          <select value={r.status} onChange={(e) => setStatus(r.id, e.target.value)}
                            className="rounded-lg border border-[#EBDDC6] bg-[#FFFBF3] px-2 py-1.5 text-xs text-[#3B2A1E] outline-none"
                            data-testid={`admin-reservation-status-${r.id}`}>
                            {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                          </select>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button onClick={() => removeReservation(r.id)} className="text-[#A08B74] transition-colors hover:text-red-600" data-testid={`admin-reservation-delete-${r.id}`} aria-label="Supprimer">
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab === "orders" && (
          <div data-testid="admin-orders-panel">
            {orders.length === 0 ? (
              <p className="py-16 text-center text-[#A08B74]" data-testid="admin-orders-empty">Aucune commande pour le moment.</p>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-[#EBDDC6]">
                <table className="w-full min-w-[760px] text-left text-sm">
                  <thead className="bg-[#FFFBF3] text-xs uppercase tracking-wider text-[#A08B74]">
                    <tr>
                      <th className="px-4 py-3">Plat</th>
                      <th className="px-4 py-3">Qté</th>
                      <th className="px-4 py-3">Total</th>
                      <th className="px-4 py-3">Client</th>
                      <th className="px-4 py-3">Retrait</th>
                      <th className="px-4 py-3">Statut</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#EBDDC6]/60">
                    {orders.map((o) => (
                      <tr key={o.id} className="bg-[#FDF6EC]" data-testid={`admin-order-row-${o.id}`}>
                        <td className="px-4 py-3">
                          <p className="font-medium">{o.dish}</p>
                          {o.notes && <p className="mt-0.5 max-w-[200px] truncate text-xs text-[#A08B74]" title={o.notes}>{o.notes}</p>}
                        </td>
                        <td className="px-4 py-3">{o.quantity}</td>
                        <td className="px-4 py-3 font-mono2 text-[#A9761F]">{o.total.toFixed(2).replace(".", ",")} €</td>
                        <td className="px-4 py-3 text-[#7A6753]">
                          <p className="text-[#3B2A1E]">{o.name}</p>
                          <p className="text-xs">{o.phone}</p>
                        </td>
                        <td className="px-4 py-3 font-mono2 text-[#A9761F]">{o.pickup_time}</td>
                        <td className="px-4 py-3">
                          <select value={o.status} onChange={(e) => setOrderStatus(o.id, e.target.value)}
                            className="rounded-lg border border-[#EBDDC6] bg-[#FFFBF3] px-2 py-1.5 text-xs text-[#3B2A1E] outline-none"
                            data-testid={`admin-order-status-${o.id}`}>
                            {Object.entries(ORDER_STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                          </select>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button onClick={() => removeOrder(o.id)} className="text-[#A08B74] transition-colors hover:text-red-600" data-testid={`admin-order-delete-${o.id}`} aria-label="Supprimer">
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab === "menu" && (
          <div data-testid="admin-menu-panel">
            <div className="mb-6 flex flex-wrap gap-3">
              <button onClick={() => setEditing({ ...EMPTY_DISH })} data-testid="admin-dish-add"
                className="inline-flex items-center gap-2 rounded-full bg-[#E07A2B] px-5 py-2.5 text-sm font-semibold text-[#3B2A1E] hover:bg-[#E89B4E]">
                <Plus size={16} /> Ajouter un plat
              </button>
              <button onClick={exportDishes} data-testid="admin-dish-export"
                className="inline-flex items-center gap-2 rounded-full border border-[#EBDDC6] bg-[#FFFBF3] px-5 py-2.5 text-sm font-medium text-[#7A6753] transition-colors hover:border-[#E07A2B] hover:text-[#E07A2B]">
                <Download size={16} /> Exporter la carte (CSV)
              </button>
            </div>

            {editing && (
              <form onSubmit={saveDish} className="mb-8 rounded-xl border border-[#EBDDC6] bg-[#FFFBF3] p-6" data-testid="admin-dish-form">
                <h2 className="font-display mb-4 text-lg font-semibold">{editing.id ? "Modifier le plat" : "Nouveau plat"}</h2>
                <div className="grid gap-3 sm:grid-cols-2">
                  <input required placeholder="Nom (FR)" value={editing.name_fr} onChange={(e) => setEditing((d) => ({ ...d, name_fr: e.target.value }))} className={inputCls} data-testid="dish-name-fr" />
                  <input required placeholder="Naam (NL)" value={editing.name_nl} onChange={(e) => setEditing((d) => ({ ...d, name_nl: e.target.value }))} className={inputCls} data-testid="dish-name-nl" />
                  <input placeholder="Description (FR)" value={editing.desc_fr} onChange={(e) => setEditing((d) => ({ ...d, desc_fr: e.target.value }))} className={inputCls} data-testid="dish-desc-fr" />
                  <input placeholder="Beschrijving (NL)" value={editing.desc_nl} onChange={(e) => setEditing((d) => ({ ...d, desc_nl: e.target.value }))} className={inputCls} data-testid="dish-desc-nl" />
                  <input required type="number" step="0.5" min="0" placeholder="Prix (€)" value={editing.price} onChange={(e) => setEditing((d) => ({ ...d, price: e.target.value }))} className={inputCls} data-testid="dish-price" />
                  <select value={editing.category} onChange={(e) => setEditing((d) => ({ ...d, category: e.target.value }))} className={inputCls} data-testid="dish-category">
                    {Object.entries(CAT_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  <input placeholder="URL de l'image (optionnel)" value={editing.image || ""} onChange={(e) => setEditing((d) => ({ ...d, image: e.target.value }))} className={`${inputCls} sm:col-span-2`} data-testid="dish-image" />
                </div>
                <div className="mt-3 flex gap-4 text-sm text-[#7A6753]">
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={editing.spicy} onChange={(e) => setEditing((d) => ({ ...d, spicy: e.target.checked }))} data-testid="dish-spicy" /> Relevé
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={editing.signature} onChange={(e) => setEditing((d) => ({ ...d, signature: e.target.checked }))} data-testid="dish-signature" /> Signature
                  </label>
                </div>
                <div className="mt-5 flex gap-3">
                  <button type="submit" className="rounded-full bg-[#E07A2B] px-5 py-2.5 text-sm font-semibold text-[#3B2A1E] hover:bg-[#E89B4E]" data-testid="dish-save">Enregistrer</button>
                  <button type="button" onClick={() => setEditing(null)} className="rounded-full border border-[#EBDDC6] px-5 py-2.5 text-sm text-[#7A6753]" data-testid="dish-cancel">Annuler</button>
                </div>
              </form>
            )}

            <div className="overflow-x-auto rounded-xl border border-[#EBDDC6]">
              <table className="w-full min-w-[680px] text-left text-sm">
                <thead className="bg-[#FFFBF3] text-xs uppercase tracking-wider text-[#A08B74]">
                  <tr>
                    <th className="px-4 py-3">Plat</th>
                    <th className="px-4 py-3">Catégorie</th>
                    <th className="px-4 py-3">Prix</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#EBDDC6]/60">
                  {dishes.map((d) => (
                    <tr key={d.id} className="bg-[#FDF6EC]" data-testid={`admin-dish-row-${d.id}`}>
                      <td className="px-4 py-3">
                        <p className="font-medium">{d.name_fr}</p>
                        <p className="text-xs text-[#A08B74]">{d.name_nl}</p>
                      </td>
                      <td className="px-4 py-3 text-[#7A6753]">{CAT_LABELS[d.category]}</td>
                      <td className="px-4 py-3 font-mono2 text-[#A9761F]">{d.price.toFixed(2)} €</td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => setEditing({ ...d, image: d.image || "" })} className="mr-3 text-[#A08B74] hover:text-[#A9761F]" data-testid={`admin-dish-edit-${d.id}`} aria-label="Modifier">
                          <Pencil size={16} />
                        </button>
                        <button onClick={() => removeDish(d.id)} className="text-[#A08B74] hover:text-red-600" data-testid={`admin-dish-delete-${d.id}`} aria-label="Supprimer">
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
